import { ArrowRight, ArrowUpDown, Copy, Share2 } from "lucide-react";
import { recordTable } from "@/components/tables/table-presentation";
import { cn } from "@/utils/cn";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import ProviderIcon from "../ProviderIcon";
import { ModelTypeBadge, ModelVisibilityBadge } from "./ModelBadges";
import type { CatalogOrder, LLMModel, TranslateFn } from "../types";

type CatalogTableProps = {
  t: TranslateFn;
  isRTL: boolean;
  loading: boolean;
  models: LLMModel[];
  currentUserId?: string;
  order: CatalogOrder;
  onSortName: () => void;
  onShare: (event: React.MouseEvent<HTMLElement>, model: LLMModel) => void;
  onCopyEndpoint: (endpoint: string) => void;
  onOpenDetail: (model: LLMModel) => void;
};

const LoadingRows = () => (
  <>
    {Array.from({ length: 10 }, (_, index) => (
      <tr key={index} data-testid="ai-catalog-loading-row" className={recordTable.row}>
        <td className={recordTable.cell}>
          <div className="flex items-center gap-2">
            <Skeleton className="size-6 shrink-0 rounded-full" />
            <div className="min-w-0 space-y-1">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-3 w-48 max-w-full" />
            </div>
          </div>
        </td>
        <td className={recordTable.cell}>
          <Skeleton className="h-6 w-32 rounded-lg" />
        </td>
        <td className={recordTable.cell}>
          <Skeleton className="h-3 w-48 max-w-full" />
        </td>
        <td className={recordTable.cell}>
          <div className="flex justify-end">
            <Skeleton className="size-6" />
          </div>
        </td>
      </tr>
    ))}
  </>
);

const CatalogTable = ({
  t,
  isRTL,
  loading,
  models,
  currentUserId,
  order,
  onSortName,
  onShare,
  onCopyEndpoint,
  onOpenDetail,
}: CatalogTableProps) => (
  <div className={recordTable.container}>
    <table className={cn(recordTable.table, "min-w-[792px] table-fixed")}>
      <colgroup>
        <col />
        <col className="w-40" />
        <col className="w-[200px]" />
        <col className="w-[132px]" />
      </colgroup>
      <thead>
        <tr className={recordTable.header}>
          <th
            scope="col"
            className={recordTable.headerCell}
            aria-sort={order === "asc" ? "ascending" : "descending"}
          >
            <button
              type="button"
              data-testid="ai-catalog-sort-name-button"
              onClick={onSortName}
              className="inline-flex items-center gap-2 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {t("predefinedModels.modelInformation")}
              <ArrowUpDown aria-hidden="true" className="size-4 text-muted-foreground" />
            </button>
          </th>
          <th scope="col" className={recordTable.headerCell}>
            {t("predefinedModels.type")}
          </th>
          <th scope="col" className={recordTable.headerCell}>
            {t("registerModelForRag.endpointUrl")}
          </th>
          <th scope="col" className={recordTable.headerCell}>
            <span className="sr-only">{t("common.actions")}</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {loading ? (
          <LoadingRows />
        ) : models.length ? (
          models.map((model, index) => (
            <tr
              key={model.id}
              className={cn(recordTable.row, "group")}
            >
              <td className={recordTable.cell}>
                <div className="flex min-w-0 items-center gap-2">
                  <div className="relative shrink-0">
                    <ProviderIcon provider={model.provider} />
                    <ModelVisibilityBadge model={model} />
                  </div>
                  <div className="min-w-0">
                    <p className={cn(recordTable.primary, "break-words")}>
                      {model.name}
                    </p>
                    <p className={cn(recordTable.secondary, "break-words")}>
                      {model.id}
                    </p>
                  </div>
                </div>
              </td>
              <td className={recordTable.cell}>
                <ModelTypeBadge model={model} t={t} />
              </td>
              <td className={recordTable.cell}>
                <p className={cn(recordTable.secondary, "truncate")}>
                  {model.endpoint}
                </p>
              </td>
              <td className={recordTable.cell}>
                <div className={recordTable.actions}>
                  {!model.is_public && model.user_id === currentUserId ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      data-testid={`ai-catalog-share-model-${index}`}
                      aria-label={`${t("common.share")} ${model.name}`}
                      className={recordTable.action}
                      onClick={(event) => {
                        event.stopPropagation();
                        onShare(event, model);
                      }}
                    >
                      <Share2 aria-hidden="true" className="size-4" />
                    </Button>
                  ) : null}
                  {model.endpoint ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      data-testid={`ai-catalog-copy-endpoint-${index}`}
                      aria-label={`${t("common.copy")} ${model.name}`}
                      className={recordTable.action}
                      onClick={(event) => {
                        event.stopPropagation();
                        onCopyEndpoint(model.endpoint || "");
                      }}
                    >
                      <Copy aria-hidden="true" className="size-4" />
                    </Button>
                  ) : null}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    data-testid={`ai-catalog-open-detail-${index}`}
                    aria-label={`${t("common.view")} ${model.name}`}
                    className={cn(recordTable.action, "opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100")}
                    onClick={(event) => {
                      event.stopPropagation();
                      onOpenDetail(model);
                    }}
                  >
                    <ArrowRight
                      aria-hidden="true"
                      className={`size-4 ${isRTL ? "scale-x-[-1]" : ""}`}
                    />
                  </Button>
                </div>
              </td>
            </tr>
          ))
        ) : (
          <tr>
            <td
              colSpan={4}
              className={cn(recordTable.cell, "h-24 text-center text-muted-foreground")}
            >
              {t("common.noDataAvailable")}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

export default CatalogTable;
